import React, { useCallback, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { SearchObjectType } from '@/apollo/graphql'
import { Button, Details, TableHeader } from '@/components'
import containerStyles from '@/components/ChangeLogContainer.module.scss'
import ChangeLogForm from '@/components/ChangeLogForm'
import ChangeLogRow from '@/components/ChangeLogRow'
import { useChangeLog } from '@/hooks/useChangeLog'
import { t } from '@/i18n'
import { ICustomer, Maybe, OrderType } from '@/types'
import {
  carsPath,
  generatePath,
  invoicesPath,
  openInNewTab,
  sort,
} from '@/utils'

import FileListButton from '@/components/buttons/FileListButton'
import FileUploadButton from '@/components/buttons/FileUploadButton'
import { API_ENDPOINT } from '@/constants'
import DetailsRow from '@/pages/Main/components/DetailsRow'
import { useSelect as useCars } from '../Cars/useSelect'
import InvoiceRow from '../Invoices/components/InvoiceRow'
import { useSelect as useInvoices } from '../Invoices/useSelect'
import CarRow from './components/carRow'
import CustomerForm from './form'
import { useData } from './useData'
import { buildData } from './utils'

const columns = [
  {
    sortKey: 'id',
    key: 'invoice_number',
  },
]

const MainInformation: React.FC = () => {
  const navigate = useNavigate()
  const { customerId } = useParams()
  const [sortKey, setKey] = useState<string>()
  const [orderBy, setOrderBy] = useState<OrderType>()
  const { data: invoices, loading: invoicesLoading } = useInvoices(
    customerId ? Number(customerId) : undefined,
  )
  const { data: cars, loading: carsLoading } = useCars(Number(customerId))
  const [isEditing, setIsEditing] = useState<boolean>(false)
  const [isAddingChangeLog, setIsAddingChangeLog] = useState<boolean>(false)
  const {
    data: customerData,
    loading,
    submitting,
    onSubmit,
  } = useData({ id: customerId })

  const {
    changeLogs,
    loading: changeLogsLoading,
    submitting: changeLogSubmitting,
    addChangeLog,
  } = useChangeLog({ customerId: customerId ? Number(customerId) : undefined })

  const sortingBy = useCallback((key?: string, by?: OrderType) => {
    setKey(key)
    setOrderBy(by)
  }, [])

  const navigateTo = useCallback(
    (path: string, node?: Maybe<SearchObjectType>) =>
      navigate(generatePath('../' + path), {
        replace: true,
        state: { id: node?.id },
      }),
    [navigate],
  )

  const onSubmitForm = useCallback(
    (d: ICustomer) => {
      onSubmit(`customers/customers/${customerId}/`, true, () =>
        setIsEditing(false),
      )(d)
    },
    [customerId, onSubmit],
  )

  const onSubmitChangeLog = useCallback(
    async (data: any) => {
      await addChangeLog(data)
      setIsAddingChangeLog(false)
    },
    [addChangeLog],
  )

  const details = useMemo(() => buildData(customerData), [customerData])
  const _invoices = useMemo(() => invoices.slice(0, 7), [invoices])

  if (isEditing) {
    return (
      <>
        <div className="title-box">
          <h1>{t('edit_customer')}</h1>
        </div>
        <div className="row customer-info">
          <div className="col w-100">
            <CustomerForm
              loading={loading}
              details={details}
              isEditing={isEditing}
              submitting={submitting}
              onSubmit={onSubmitForm}
              initialData={customerData}
              onCancel={() => setIsEditing(false)}
            />
          </div>
        </div>
      </>
    )
  }
  return (
    <>
      <div className="title-box">
        <h1>{t('main_information')}</h1>
        <div className={'row gap align-items-center'}>
          <Button className="main-btn white" onClick={() => setIsEditing(true)}>
            <span className="icon-edit"></span>
            {t('edit_customer')}
          </Button>
          <Button
            className="main-btn white print-form-button"
            onClick={() =>
              openInNewTab(
                `${API_ENDPOINT}/payment/customer-buyout/${customerId}/pdf/`,
              )
            }
          >
            {t('print_buyout_form')}
          </Button>
          <FileUploadButton
            className="main-btn white"
            contextType="customer"
            contextId={customerId}
            title={t('upload_customer_files')}
          />
          <FileListButton
            className="main-btn white"
            contextType="customer"
            contextId={customerId || ''}
            title={t('view_files')}
          />
        </div>
      </div>
      <div className="row customer-info">
        <div className="col w-67">
          <Details
            loading={loading}
            data={details.general_info}
            icon="icon-information"
            title={t('general_info')}
            Row={DetailsRow}
          />
          <Details
            loading={loading}
            icon="icon-details"
            data={details.financial_details}
            title={t('financial_details')}
            Row={DetailsRow}
          />

          <Details
            showNoData
            withButton
            Wrapper="table"
            icon="icon-file"
            Row={InvoiceRow}
            RowParent="tbody"
            title={t('invoices')}
            loading={invoicesLoading}
            data={_invoices
              .sort((a, b) => sort(a, b, sortKey, orderBy))
              .slice(0, 8)}
            wrapperClassName="info-box w-100"
            dataBoxWrapperClassName="info-table big"
            onClickViewAll={() => navigateTo(invoicesPath)}
            Header={() => (
              <TableHeader
                columns={columns}
                onSort={sortingBy}
                orderedBy={orderBy}
              />
            )}
            onRowClick={(node: Maybe<SearchObjectType>) =>
              navigateTo(invoicesPath, node)
            }
          />
        </div>
        <div className="col w-33 h-max-content">
          <Details
            loading={loading}
            data={details.contact_details}
            icon="icon-support"
            title={t('contact_details')}
            wrapperClassName="info-box w-100"
            Row={DetailsRow}
          />

          <Details
            withButton
            Row={CarRow}
            showNoData
            icon="icon-car"
            title={t('cars')}
            loading={carsLoading}
            data={cars.slice(0, 3)}
            wrapperClassName="info-box w-100"
            dataBoxWrapperClassName="cars-list"
            onClickViewAll={() => navigateTo(carsPath)}
            onRowClick={(node: Maybe<SearchObjectType>) =>
              navigateTo(carsPath, node)
            }
          />

          <div className="info-box w-100">
            <div className={containerStyles['info-header']}>
              <div className={containerStyles['info-title']}>
                <span className="icon-history"></span>
                <h3>{t('change_logs')}</h3>
              </div>
              <div className={containerStyles['info-actions']}>
                <Button
                  className="main-btn white"
                  onClick={() => setIsAddingChangeLog(true)}
                >
                  <span className="icon-plus"></span>
                  {t('add_change_log')}
                </Button>
                {changeLogs.length > 5 && (
                  <Button
                    className="main-btn blank"
                    onClick={() => {
                      // TODO: Navigate to full change logs page
                      console.log('View all change logs', changeLogs)
                    }}
                  >
                    {t('viewAll')}
                    <span className="icon-arrow"></span>
                  </Button>
                )}
              </div>
            </div>
            <div className="info-data">
              {isAddingChangeLog ? (
                <ChangeLogForm
                  onSubmit={onSubmitChangeLog}
                  onCancel={() => setIsAddingChangeLog(false)}
                  submitting={changeLogSubmitting}
                />
              ) : (
                <div className={containerStyles['change-logs-container']}>
                  {changeLogsLoading ? (
                    <div className={containerStyles['fetch-detail']}>
                      <img
                        className={containerStyles['search-loading-indicator']}
                        src="/src/assets/images/loading_indicator.gif"
                        alt="Loading..."
                      />
                    </div>
                  ) : changeLogs.length > 0 ? (
                    changeLogs
                      .slice(0, 5)
                      .map((changeLog, index) => (
                        <ChangeLogRow
                          key={changeLog.id || index}
                          node={changeLog}
                        />
                      ))
                  ) : (
                    <div className={containerStyles['no-change-logs']}>
                      <p>{t('no_change_logs_yet')}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default MainInformation
